import { useState, useEffect } from "react";
import { HexColorPicker } from "react-colorful";
import dayjs from "dayjs";

import {
  Box,
  Tooltip,
  Grid,
  Typography,
  ButtonBase,
  IconButton,
  Popper,
  StyledEngineProvider,
  CircularProgress,
  ThemeProvider,
  useTheme
} from "@mui/material";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import ClearIcon from "@mui/icons-material/Clear";
import HelpIcon from "@mui/icons-material/Help";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRibbon } from "@fortawesome/free-solid-svg-icons";

import {
  TextField,
  DefaultPaper,
  LoadingButton
} from "@aclymatepackages/atoms";
import { ucFirstLetters, formatDollars } from "@aclymatepackages/formatters";
import {
  useLayoutHelpers,
  mergeDarkTheme,
  mainTheme,
  isColorDarkTheme
} from "@aclymatepackages/themes";
import { editRowData } from "@aclymatepackages/array-immutability-helpers";

import DollarInput from "../../atoms/inputs/DollarInput";
import FeatureErrorBoundary from "../../atoms/FeatureErrorBoundary";
import Card from "../../atoms/mui/Card";
import EditStartDateButton from "../../atoms/accounting/EditStartDateButton";

import LogoUploadDropzone from "../../modules/file-uploaders/LogoUploadDropzone";
import CompanyNameRow from "../../modules/settings/CompanyNameRow";
import CompanyAddressRow from "../../modules/settings/CompanyAddressRow";
import CompanyIndustryRow from "../../modules/settings/CompanyIndustryRow";
import CompanyEmployeeCountRow from "../../modules/settings/CompanyEmployeeCountRow";
import CompanyDataListItem from "../../modules/settings/CompanyDataListItem";
import InputMeasurementSystemRow from "../../modules/settings/InputMeasurementSystemRow";
import DisplayMeasurementSystemRow from "../../modules/settings/DisplayMeasurementSystemRow";

import {
  useAccountData,
  useCachedFirebaseCrud
} from "../../../helpers/firebase";
import useAccountingData from "../../../helpers/hooks/accountingData";
import { useRevenueYears } from "../../../helpers/hooks/companyData";

const BrandColorDisplay = ({ type, color, ...gridProps }) => {
  const { theme } = useLayoutHelpers();
  const { updateAccountData } = useCachedFirebaseCrud();

  const [saveLoading, setSaveLoading] = useState(false);
  const [pickerColor, setPickerColor] = useState(
    color || theme.palette.backgroundGray.main
  );
  const [anchorEl, setAnchorEl] = useState(null);

  const hexColorRegexpTest = RegExp("^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$").test(
    pickerColor
  );

  const onColorSave = async () => {
    setSaveLoading(true);
    await updateAccountData({ [`branding.${type}Color`]: pickerColor });
    setSaveLoading(false);
    return setAnchorEl(null);
  };

  return (
    <Grid item {...gridProps}>
      <Popper open={!!anchorEl} anchorEl={anchorEl} placement="left-start">
        <DefaultPaper>
          <Grid container spacing={1} direction="column">
            <Grid
              item
              container
              justifyContent="space-between"
              spacing={2}
              alignItems="center"
            >
              <Grid item>
                <Typography
                  variant="subtitle2"
                  align="center"
                >{`Select your ${type} color`}</Typography>
              </Grid>
              <Grid item>
                <IconButton size="small" onClick={() => setAnchorEl(null)}>
                  <CancelIcon />
                </IconButton>
              </Grid>
            </Grid>
            <Grid item>
              <HexColorPicker color={pickerColor} onChange={setPickerColor} />
            </Grid>
            <Grid item>
              <TextField
                value={pickerColor}
                setValue={setPickerColor}
                error={!hexColorRegexpTest}
              />
            </Grid>
            <Grid item container justifyContent="center">
              <Grid item>
                <LoadingButton
                  label="Save Color"
                  isLoading={saveLoading}
                  color="primary"
                  disabled={!hexColorRegexpTest}
                  onClick={onColorSave}
                />
              </Grid>
            </Grid>
          </Grid>
        </DefaultPaper>
      </Popper>
      <ButtonBase
        style={{ width: "100%" }}
        onClick={({ currentTarget }) => setAnchorEl(currentTarget)}
      >
        <DefaultPaper
          style={{
            backgroundColor: color || theme.palette.backgroundGray.main,
            width: "100%"
          }}
        >
          <StyledEngineProvider injectFirst>
            <ThemeProvider
              theme={isColorDarkTheme(color) ? mergeDarkTheme : mainTheme}
            >
              <Box display="flex" alignItems="center" justifyContent="center">
                <div>
                  <Typography
                    variant="subtitle1"
                    align="center"
                    color="textPrimary"
                  >{`${ucFirstLetters(type)}${
                    color ? `: ${color}` : ""
                  }`}</Typography>
                  <Typography
                    variant="body2"
                    align="center"
                    color="textSecondary"
                  >
                    {color
                      ? "Click to edit"
                      : `Click to select your ${type} color`}
                  </Typography>
                </div>
              </Box>
            </ThemeProvider>
          </StyledEngineProvider>
        </DefaultPaper>
      </ButtonBase>
    </Grid>
  );
};

const CompanyTextBlockEditor = ({
  title,
  annotation,
  dataLoading,
  text,
  field
}) => {
  const { updateAccountData } = useCachedFirebaseCrud();

  const [statement, setStatement] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const onSave = async () => {
    setSaveLoading(true);
    await updateAccountData({ [field]: statement });
    setIsEditing(false);
    return setSaveLoading(false);
  };

  return (
    <Grid container direction="column">
      <Grid item container justifyContent="space-between">
        <Grid item>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <Typography variant="h5" display="inline">
                {title}
              </Typography>
            </Grid>
            <Grid item>
              <Tooltip title={annotation}>
                <HelpIcon />
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container spacing={1}>
            <Grid item>
              {saveLoading ? (
                <CircularProgress size={22} color="inherit" />
              ) : isEditing ? (
                <IconButton size="small">
                  <SaveIcon />
                </IconButton>
              ) : (
                <IconButton size="small" onClick={() => setIsEditing(true)}>
                  <EditIcon />
                </IconButton>
              )}
            </Grid>
            {isEditing && (
              <Grid item>
                <IconButton
                  size="small"
                  onClick={() => {
                    setIsEditing(false);
                    return setStatement("");
                  }}
                >
                  <CancelIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        {isEditing ? (
          <TextField
            value={statement}
            setValue={setStatement}
            isLoading={dataLoading}
            onBlur={onSave}
            multiline
            rows={4}
          />
        ) : (
          <Typography variant="body2">{text}</Typography>
        )}
      </Grid>
    </Grid>
  );
};

const BrandingCard = () => {
  const [{ branding, description }, companyDataLoading] = useAccountData();
  const { primaryColor, secondaryColor, companyStatement } = branding || {};

  return (
    <Card
      cardType="layout"
      gridProps={{ sm: 12 }}
      title="Branding"
      icon={<FontAwesomeIcon icon={faRibbon} />}
      subtitle="Customize how you want to display your company's presence on our website"
      isLoading={companyDataLoading}
      content={
        <Grid container spacing={2} alignItems="stretch">
          <Grid item sm={6}>
            <Box sx={{ aspectRatio: "1 / 1", maxHeight: 300, width: "100%" }}>
              <LogoUploadDropzone
                logoField="logoUrl"
                imageName="primary logo"
              />
            </Box>
          </Grid>
          <Grid item sm={6}>
            <Box sx={{ aspectRatio: "1 / 1", maxHeight: 300, width: "100%" }}>
              <LogoUploadDropzone
                logoField="secondaryLogoUrl"
                imageName="secondary logo"
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6">Brand Colors</Typography>
          </Grid>
          <BrandColorDisplay type="primary" color={primaryColor} sm={6} />
          <BrandColorDisplay type="secondary" color={secondaryColor} sm={6} />
          <Grid item xs={12}>
            <CompanyTextBlockEditor
              title="Company Description"
              annotation="This is how you describe your company to your vendors and customers"
              dataLoading={companyDataLoading}
              text={description}
              field="description"
            />
          </Grid>
          <Grid item xs={12}>
            <CompanyTextBlockEditor
              title="Company Statement"
              annotation="This is the statement that will show up at the top of any of your reports"
              dataLoading={companyDataLoading}
              text={companyStatement}
              field="branding.companyStatement"
            />
          </Grid>
        </Grid>
      }
    />
  );
};

const StartDateRow = () => {
  const [{ startDate }] = useAccountData();
  const [{ baselineDate }] = useAccountingData();

  return (
    <CompanyDataListItem
      editingComponent={<EditStartDateButton disabled={!!baselineDate} />}
      primaryText={dayjs(startDate).year()}
      secondaryText="Accounting Start Year"
      editDisabled={!!baselineDate}
    />
  );
};

const RevenueListItem = ({ year, revenue, isEditing, editRevenueYear }) => {
  const { palette } = useTheme();

  return (
    <Grid item container spacing={2} alignItems="center">
      <Grid item>
        {!revenue ? (
          <ClearIcon style={{ color: palette.error.main }} />
        ) : (
          <CheckCircleIcon style={{ color: palette.secondary.main }} />
        )}
      </Grid>
      <Grid item>
        <Typography
          variant="h4"
          color="textSecondary"
          style={{ width: "48px" }}
        >
          {year}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        {isEditing ? (
          <DollarInput
            label="Revenue"
            value={revenue}
            setValue={(value) => editRevenueYear("revenue", value)}
          />
        ) : (
          <Typography variant="h6" color={!revenue ? "error" : "auto"}>
            {revenue
              ? formatDollars(revenue)
              : `Please enter your ${year} revenue`}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

const CompanyRevenueCard = () => {
  const { updateAccountData } = useCachedFirebaseCrud();
  const [revenueYears, revenueYearsLoading] = useRevenueYears();

  const [isEditing, setIsEditing] = useState(false);
  const [editRevenueYears, setEditRevenueYears] = useState([]);

  useEffect(() => {
    if (!revenueYearsLoading && !editRevenueYears.length) {
      setEditRevenueYears(revenueYears);
    }
  }, [editRevenueYears, revenueYears, revenueYearsLoading]);

  const onCancel = () => {
    setEditRevenueYears(revenueYears);
    return setIsEditing(false);
  };

  const onSave = () => {
    updateAccountData({ revenueYears: editRevenueYears });
    return setIsEditing(false);
  };

  return (
    <Card
      cardType="layout"
      gridProps={{ sm: 6 }}
      title="Revenue History"
      subtitle="Entering your revenue history is optional but helps us provide you with better reporting and benchmarking that's required for some emissions standards"
      icon={<AttachMoneyIcon />}
      isLoading={revenueYearsLoading}
      action={
        <>
          {isEditing ? (
            <Grid container>
              <Grid item>
                <IconButton onClick={onCancel}>
                  <CancelIcon />
                </IconButton>
              </Grid>
              <Grid item>
                <IconButton onClick={onSave}>
                  <SaveIcon />
                </IconButton>
              </Grid>
            </Grid>
          ) : (
            <IconButton onClick={() => setIsEditing(true)}>
              <EditIcon />
            </IconButton>
          )}
        </>
      }
      content={
        <Grid container direction="column" spacing={1}>
          {editRevenueYears.map((revenueYear, idx) => (
            <RevenueListItem
              key={`revenue-list-item-${idx}`}
              isEditing={isEditing}
              editRevenueYear={editRowData(idx, setEditRevenueYears)}
              {...revenueYear}
            />
          ))}
        </Grid>
      }
    />
  );
};

const DashboardCompanyData = () => {
  const [companyData, companyDataLoading] = useAccountData();
  const {
    primaryOffice,
    startDate,
    address,
    isRemote,
    employeeCount,
    employeeCountConfirmed
  } = companyData;

  const companyDataSubtitle = () => {
    if (!primaryOffice || !startDate) {
      return "Please finish setting up your company profile.";
    }
    return "Your company profile is all up to date. Click on any field to edit it.";
  };

  return (
    <FeatureErrorBoundary featureName="Company Settings">
      <Grid container spacing={2}>
        <Grid item sm={6}>
          <Grid item container direction="column" spacing={2}>
            <Card
              cardType="layout"
              gridProps={{ sm: 6 }}
              title="Company Information"
              subtitle={companyDataSubtitle()}
              icon={<LocationCityIcon />}
              isLoading={companyDataLoading}
              contentType="list"
              action={
                isRemote &&
                !address?.description && (
                  <div>
                    <Tooltip title="You have missing company information. Add it below.">
                      <WarningIcon color="error" />
                    </Tooltip>
                  </div>
                )
              }
            >
              <CompanyNameRow {...companyData} />
              <CompanyAddressRow {...companyData} />
              <CompanyIndustryRow {...companyData} />
              {employeeCount && !employeeCountConfirmed && (
                <CompanyEmployeeCountRow {...companyData} />
              )}
              <StartDateRow />
              <InputMeasurementSystemRow {...companyData} />
              <DisplayMeasurementSystemRow {...companyData} />
            </Card>
            <CompanyRevenueCard />
          </Grid>
        </Grid>
        <Grid item sm={6}>
          <BrandingCard />
        </Grid>
      </Grid>
    </FeatureErrorBoundary>
  );
};
export default DashboardCompanyData;
